import {
	createHoverActionsContainer,
	ICON_BUTTON_ACTIONS,
} from "../components/header-components/ActionButtons";
import { HeaderElement } from "../types";

// IconButton Component as HeaderElement Object - Default Configuration
const iconButtonElement: HeaderElement = {
	id: "iconbutton-1",
	type: "IconButton",
	tagName: "div",
	data: {
		// Component settings/configuration
		icon: "menu", // Default icon
		size: "md", // "xs", "sm", "md", "lg", "xl"
		shape: "rounded", // "box", "circle", "rounded"
		background: "transparent",
		hoverColor: "#f3f4f6",
		borderRadius: 4,
		hoverAnimation: "pop", // "spin", "pop", "flash", "fade", "none"

		// Component behavior settings
		isSelectable: true,
		isDeletable: true,
		hasHoverActions: true,

		// Event handlers
		onClick: "handleIconButtonClick",
		onRemove: "handleIconButtonRemove",
		onEdit: "handleIconButtonEdit",
	},
	className: "iconbutton-container",
	style: {
		position: "relative",
		cursor: "pointer",
		padding: "8px",
	},
	props: {
		onClick: "handleClick",
		role: "button",
		tabIndex: 0,
	},
	children: [
		// Icon Button Element
		{
			id: "iconbutton-btn",
			type: "Button",
			tagName: "button",
			data: {
				icon: "menu",
				size: "md",
				shape: "rounded",
				hoverAnimation: "pop",
			},
			className: "icon-button shape-rounded size-md hover-pop",
			props: {
				onClick: "handleButtonClick",
			},
			children: [
				// Icon Element
				{
					id: "iconbutton-icon",
					type: "Icon",
					tagName: "svg",
					data: {
						icon: "menu",
					},
					className: "button-icon",
					props: {
						width: "16",
						height: "16",
						viewBox: "0 0 24 24",
						fill: "none",
						stroke: "currentColor",
					},
					children: [],
				},
			],
		},

		// Hover Actions Container
		{
			id: "iconbutton-hover-actions",
			type: "HoverActions",
			tagName: "div",
			data: {
				showOnHover: true,
			},
			className: "hover-actions",
			style: {
				position: "absolute",
				top: "0",
				right: "0",
			},
			children: [
				// Edit Button
				{
					id: "iconbutton-edit-button",
					type: "ActionButton",
					tagName: "button",
					data: {
						action: "edit",
						icon: "⚙️",
					},
					className: "action-btn edit-btn",
					props: {
						onClick: "handleEditClick",
					},
					children: [
						{
							id: "edit-icon",
							type: "Icon",
							tagName: "span",
							data: {
								icon: "⚙️",
							},
							className: "btn-icon",
							children: [],
						},
					],
				},

				// Remove Button
				{
					id: "iconbutton-remove-button",
					type: "ActionButton",
					tagName: "button",
					data: {
						action: "remove",
						icon: "🗑️",
					},
					className: "action-btn remove-btn",
					props: {
						onClick: "handleRemoveClick",
					},
					children: [
						{
							id: "remove-icon",
							type: "Icon",
							tagName: "span",
							data: {
								icon: "🗑️",
							},
							className: "btn-icon",
							children: [],
						},
					],
				},
			],
		},
	],

	css: `
        .iconbutton-container {
            position: relative;
            cursor: pointer;
            padding: 8px;
        }
        
        .iconbutton-container.selected {
            border: 2px dashed #2196f3;
        }
        
        /* Base Icon Button Styles */
        .icon-button {
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: transparent;
            border: none;
            cursor: pointer;
            transition: all 0.3s ease;
        }
        
        /* Size Variations */
        .icon-button.size-xs {
            width: 24px;
            height: 24px;
            padding: 4px;
        }
        
        .icon-button.size-sm {
            width: 32px;
            height: 32px;
            padding: 6px;
        }
        
        .icon-button.size-md {
            width: 40px;
            height: 40px;
            padding: 8px;
        }
        
        .icon-button.size-lg {
            width: 48px;
            height: 48px;
            padding: 10px;
        }
        
        .icon-button.size-xl {
            width: 56px;
            height: 56px;
            padding: 12px;
        }
        
        /* Shape Variations */
        .icon-button.shape-box {
            border-radius: 0;
        }
        
        .icon-button.shape-circle {
            border-radius: 50%;
        }
        
        .icon-button.shape-rounded {
            border-radius: 4px;
        }
        
        /* Hover Colors */
        .icon-button:hover {
            background-color: #f3f4f6;
        }
        
        .icon-button.hover-color-blue:hover {
            background-color: #dbeafe;
        }
        
        .icon-button.hover-color-green:hover {
            background-color: #dcfce7;
        }
        
        .icon-button.hover-color-red:hover {
            background-color: #fee2e2;
        }
        
        /* Hover Animations */
        .icon-button.hover-spin:hover {
            transform: rotate(180deg);
            transition: transform 0.5s ease;
        }
        
        .icon-button.hover-pop:hover {
            transform: scale(1.1);
            transition: transform 0.3s ease;
        }
        
        .icon-button.hover-flash:hover {
            opacity: 0.7;
            animation: flash 0.5s ease;
        }
        
        .icon-button.hover-fade:hover {
            opacity: 0.7;
            transition: opacity 0.3s ease;
        }
        
        @keyframes flash {
            0%, 100% { opacity: 0.7; }
            50% { opacity: 1; }
        }
        
        /* Icon Styles */
        .button-icon {
            width: 16px;
            height: 16px;
            color: currentColor;
        }
        
        .size-xs .button-icon {
            width: 12px;
            height: 12px;
        }
        
        .size-sm .button-icon {
            width: 14px;
            height: 14px;
        }
        
        .size-md .button-icon {
            width: 16px;
            height: 16px;
        }
        
        .size-lg .button-icon {
            width: 20px;
            height: 20px;
        }
        
        .size-xl .button-icon {
            width: 24px;
            height: 24px;
        }
        
        /* Hover Actions */
        .hover-actions {
            position: absolute;
            top: 0;
            right: 0;
            opacity: 0;
            background-color: rgba(0, 0, 0, 0.7);
            padding: 4px;
            border-radius: 4px;
            display: flex;
            transition: opacity 0.2s ease;
        }
        
        .iconbutton-container:hover .hover-actions {
            opacity: 1;
        }
        
        .action-btn {
            padding: 4px 8px;
            margin: 0 2px;
            color: white;
            background: none;
            border: none;
            cursor: pointer;
            border-radius: 2px;
            transition: all 0.2s ease;
        }
        
        .edit-btn:hover {
            color: #64b5f6;
            transform: scale(1.1);
        }
        
        .remove-btn:hover {
            color: #f48fb1;
            transform: scale(1.1);
        }
        
        .btn-icon {
            font-size: 12px;
            display: inline-block;
        }
    `,
};

// Factory function to create icon button elements with different configurations
const createIconButtonElement = (
	config: {
		id: string;
		icon?: string;
		size?: "xs" | "sm" | "md" | "lg" | "xl";
		shape?: "box" | "circle" | "rounded";
		background?: string;
		hoverColor?: string;
		hoverAnimation?: "spin" | "pop" | "flash" | "fade" | "none";
		borderRadius?: number;
		customStyles?: React.CSSProperties;
	},
	publishMode = false
): HeaderElement => {
	const size = config.size || "md";
	const shape = config.shape || "rounded";
	const hoverAnimation = config.hoverAnimation || "pop";
	const icon = config.icon || "menu";

	// Build button classes
	const buttonClasses = ["icon-button", `size-${size}`, `shape-${shape}`];

	if (hoverAnimation && hoverAnimation !== "none") {
		buttonClasses.push(`hover-${hoverAnimation}`);
	}

	// Determine hover color class
	let hoverColorClass = "";
	if (config.hoverColor) {
		if (config.hoverColor.includes("blue"))
			hoverColorClass = "hover-color-blue";
		else if (config.hoverColor.includes("green"))
			hoverColorClass = "hover-color-green";
		else if (config.hoverColor.includes("red"))
			hoverColorClass = "hover-color-red";
	}

	if (hoverColorClass) {
		buttonClasses.push(hoverColorClass);
	}

	// Calculate icon size based on button size
	const iconSizes = {
		xs: { width: "12", height: "12" },
		sm: { width: "14", height: "14" },
		md: { width: "16", height: "16" },
		lg: { width: "20", height: "20" },
		xl: { width: "24", height: "24" },
	};

	const iconSize = iconSizes[size] || iconSizes["md"];

	const element: HeaderElement = {
		id: config.id,
		type: "IconButton",
		tagName: "div",
		data: {
			icon: icon,
			size: size,
			shape: shape,
			background: config.background || "transparent",
			hoverColor: config.hoverColor || "#f3f4f6",
			hoverAnimation: hoverAnimation,
			borderRadius: config.borderRadius || 4,
			isSelectable: true,
			isDeletable: true,
			hasHoverActions: true,
		},
		className: "iconbutton-container",
		style: {
			position: "relative",
			cursor: "pointer",
			padding: "8px",
			...config.customStyles,
		},
		children: [
			// Icon Button Element
			{
				id: `${config.id}-btn`,
				type: "Button",
				tagName: "button",
				data: {
					icon: icon,
					size: size,
					shape: shape,
					hoverAnimation: hoverAnimation,
				},
				className: buttonClasses.join(" "),
				style: {
					backgroundColor: config.background || "transparent",
				},
				props: {
					onClick: "handleButtonClick",
				},
				children: [
					// Icon Element
					{
						id: `${config.id}-icon`,
						type: "Icon",
						tagName: "svg",
						data: {
							icon: icon,
						},
						className: "button-icon",
						props: {
							width: iconSize.width,
							height: iconSize.height,
							viewBox: "0 0 24 24",
							fill: "none",
							stroke: "currentColor",
						},
						children: [],
					},
				],
			},

			// Hover Actions Container
			...[
				!publishMode
					? createHoverActionsContainer(
							config.id,
							ICON_BUTTON_ACTIONS
					  )
					: ([] as any),
			],
		],
		css: generateIconButtonCSS(config),
	};

	return element;
};

// Helper function to generate custom CSS based on configuration
function generateIconButtonCSS(config: {
	hoverColor?: string;
	borderRadius?: number;
	background?: string;
}): string {
	let customCSS = `
        .iconbutton-container {
            position: relative;
            cursor: pointer;
            padding: 8px;
        }
        
        .iconbutton-container.selected {
            border: 2px dashed #2196f3;
        }
        
        .icon-button {
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: ${config.background || "transparent"};
            border: none;
            cursor: pointer;
            transition: all 0.3s ease;
        }
        
        .size-xs { width: 24px; height: 24px; padding: 4px; }
        .size-sm { width: 32px; height: 32px; padding: 6px; }
        .size-md { width: 40px; height: 40px; padding: 8px; }
        .size-lg { width: 48px; height: 48px; padding: 10px; }
        .size-xl { width: 56px; height: 56px; padding: 12px; }
        
        .shape-box { border-radius: 0; }
        .shape-circle { border-radius: 50%; }
        .shape-rounded { border-radius: ${config.borderRadius || 4}px; }
        
        .icon-button:hover {
            background-color: ${config.hoverColor || "#f3f4f6"};
        }
        
        .hover-spin:hover { transform: rotate(180deg); transition: transform 0.5s ease; }
        .hover-pop:hover { transform: scale(1.1); transition: transform 0.3s ease; }
        .hover-flash:hover { opacity: 0.7; animation: flash 0.5s ease; }
        .hover-fade:hover { opacity: 0.7; transition: opacity 0.3s ease; }
        
        @keyframes flash {
            0%, 100% { opacity: 0.7; }
            50% { opacity: 1; }
        }
        
        .button-icon { width: 16px; height: 16px; color: currentColor; }
        .size-xs .button-icon { width: 12px; height: 12px; }
        .size-sm .button-icon { width: 14px; height: 14px; }
        .size-md .button-icon { width: 16px; height: 16px; }
        .size-lg .button-icon { width: 20px; height: 20px; }
        .size-xl .button-icon { width: 24px; height: 24px; }
        
        .hover-actions {
            position: absolute;
            top: 0;
            right: 0;
            opacity: 0;
            background-color: rgba(0, 0, 0, 0.7);
            padding: 4px;
            border-radius: 4px;
            display: flex;
            transition: opacity 0.2s ease;
        }
        
        .iconbutton-container:hover .hover-actions { opacity: 1; }
        
        .action-btn {
            padding: 4px 8px;
            margin: 0 2px;
            color: white;
            background: none;
            border: none;
            cursor: pointer;
            border-radius: 2px;
            transition: all 0.2s ease;
        }
        
        .edit-btn:hover { color: #64b5f6; transform: scale(1.1); }
        .remove-btn:hover { color: #f48fb1; transform: scale(1.1); }
        .btn-icon { font-size: 12px; display: inline-block; }
    `;

	return customCSS;
}

// Predefined common icon button configurations
const createMenuIconButton = (id: string) =>
	createIconButtonElement({
		id,
		icon: "menu",
		size: "md",
		shape: "rounded",
		hoverAnimation: "pop",
	});

const createSearchIconButton = (id: string) =>
	createIconButtonElement({
		id,
		icon: "search",
		size: "md",
		shape: "circle",
		hoverAnimation: "fade",
	});

const createProfileIconButton = (id: string) =>
	createIconButtonElement({
		id,
		icon: "user",
		size: "lg",
		shape: "circle",
		hoverAnimation: "pop",
	});

export {
	createIconButtonElement,
	createMenuIconButton,
	createProfileIconButton,
	createSearchIconButton,
	iconButtonElement,
};
